/**
 * ReadinessWidget — Loop MWEB-2
 *
 * Surfaces the merchant storefront-activation checklist from
 *   GET /api/v1/merchant/storefront/readiness
 * on Merchant Overview. Backend is the SOLE source of truth for readiness
 * (rule A) — we render, we never recompute.
 *
 * Two surfaces, one data-fetch:
 *   1. <ReadinessBanner /> — dismissible full-width banner shown while the
 *      merchant is <100% ready. Auto-hides at 100%. Dismiss is session-only
 *      (sessionStorage) and NEVER mutates readiness itself.
 *   2. <ReadinessCard />   — persistent Overview card that stays put; at
 *      100% it flips to a compact "Storefront Ready" positive state.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  CheckCircle2, Circle, AlertCircle, ArrowRight, Rocket, ShieldCheck,
  X as XIcon,
} from "lucide-react";

const DISMISS_KEY = "cy_readiness_banner_dismissed_v1";

// Map an item label → its remediation route inside the Merchant Portal.
// Keep it dumb: labels come straight from the backend contract in
// routers/storefronts.py::merchant_readiness so this table lives next to
// the UI it drives, not next to the API.
const REMEDIATION = {
  "Storefront profile":      "/merchant/storefronts",
  "Province coverage":       "/merchant/storefronts",
  "Website destination":     "/merchant/storefronts",
  "Referral tracking":       "/merchant/tracking",
  "Landing tracking":        "/merchant/integrations",
  "Conversion integration":  "/merchant/developer",
  "Active offer":            "/merchant/campaigns",
  "Mobile app":              "/merchant/storefronts",
};

function statusIcon(status) {
  if (status === "complete" || status === "active" || status === "connected") {
    return <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />;
  }
  if (status === "optional" || status === "not_configured") {
    return <Circle className="w-4 h-4 text-slate-300 shrink-0" />;
  }
  return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />;
}

function statusPill(status) {
  const cls = {
    complete:        "bg-emerald-100 text-emerald-700",
    active:          "bg-emerald-100 text-emerald-700",
    connected:       "bg-emerald-100 text-emerald-700",
    optional:        "bg-slate-100 text-slate-500",
    not_configured:  "bg-slate-100 text-slate-500",
    incomplete:      "bg-amber-100 text-amber-800",
  }[status] || "bg-slate-100 text-slate-500";
  const label = ({
    complete: "Done",
    active: "Active",
    connected: "Connected",
    optional: "Optional",
    not_configured: "Not set",
    incomplete: "Incomplete",
  })[status] || status;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

// Percentage completed across ALL checklist items (matches user-facing
// intuition of "% ready"). Excludes items that report themselves as
// `optional` from the denominator so a website-only merchant never gets
// punished for skipping the Mobile app row (rule N).
function computeProgress(checklist) {
  const relevant = checklist.filter(
    (c) => c.status !== "optional" && c.status !== "not_configured"
  );
  if (relevant.length === 0) return 100;
  const done = relevant.filter(
    (c) => c.status === "complete" || c.status === "active" || c.status === "connected"
  ).length;
  return Math.round((done / relevant.length) * 100);
}

function firstIncomplete(checklist) {
  return checklist.find((c) => c.blocking && c.status !== "complete") ||
         checklist.find((c) => c.status === "incomplete") ||
         null;
}

export function useReadiness() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/v1/merchant/storefront/readiness")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, []);
  return { data, error };
}

// -----------------------------------------------------------------------
// Banner
// -----------------------------------------------------------------------
export function ReadinessBanner({ readiness }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  if (!readiness) return null;
  const progress = computeProgress(readiness.checklist || []);
  const remainingCount = (readiness.checklist || []).filter(
    (c) => c.blocking && c.status !== "complete"
  ).length;
  // Hide when perfect — the card handles the "Ready" state.
  if (readiness.ready && remainingCount === 0) return null;
  if (dismissed) return null;
  const next = firstIncomplete(readiness.checklist || []);
  const nextHref = (next && REMEDIATION[next.label]) || "/merchant/storefronts";

  function dismiss() {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  }

  return (
    <div
      className="pf-card relative overflow-hidden mb-6 border-amber-200 bg-gradient-to-r from-amber-50 via-white to-cyan-50"
      data-testid="readiness-banner"
    >
      <div className="absolute -top-16 -right-16 w-64 h-64 bg-amber-200/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-cyan-200/40 rounded-full blur-3xl" />
      <button
        onClick={dismiss}
        aria-label="Dismiss readiness banner for this session"
        className="absolute top-3 right-3 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 z-10"
        data-testid="readiness-banner-dismiss"
      >
        <XIcon className="w-4 h-4" />
      </button>

      <div className="relative p-5 md:p-6 grid md:grid-cols-[1fr_auto] gap-5 items-center">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Rocket className="w-4 h-4 text-amber-700" />
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-700">
              Almost there
            </div>
          </div>
          <div className="font-display text-2xl md:text-3xl tracking-tight text-slate-900" data-testid="readiness-banner-progress">
            Your storefront is <span className="tabular-nums">{progress}%</span> ready
          </div>
          <p className="text-sm text-slate-600 mt-1" data-testid="readiness-banner-summary">
            {remainingCount === 0
              ? "You're all set — publish your next offer to get discovered."
              : <>Complete <b>{remainingCount}</b> more step{remainingCount === 1 ? "" : "s"} before going live.</>
            }
          </p>
          {/* Progress bar */}
          <div className="mt-3 h-2 w-full max-w-md rounded-full bg-white/70 overflow-hidden border border-slate-200">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-cyan-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="readiness-banner-progress-bar"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={nextHref}
            className="pf-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 whitespace-nowrap"
            data-testid="readiness-banner-cta"
            title={next?.hint || "Continue setup"}
          >
            Continue setup <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Card
// -----------------------------------------------------------------------
export function ReadinessCard({ readiness }) {
  if (!readiness) return null;
  const items = readiness.checklist || [];
  const progress = computeProgress(items);

  // 100% → compact "Ready" state (rule A: keep card visible, don't remove).
  if (readiness.ready && progress === 100) {
    return (
      <div
        className="pf-card p-5 mb-6 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-cyan-50"
        data-testid="readiness-card-ready"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-emerald-700">
              Storefront ready
            </div>
            <div className="text-sm text-slate-700 mt-0.5">
              All required steps complete — new offers will publish immediately.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const completed = items.filter(
    (c) => c.status === "complete" || c.status === "active" || c.status === "connected"
  );
  const remaining = items.filter(
    (c) => c.status !== "complete" && c.status !== "active" && c.status !== "connected"
  );

  return (
    <div className="pf-card p-5 mb-6" data-testid="readiness-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-cyan-700">
            Activation checklist
          </div>
          <h3 className="font-display text-xl text-slate-900 mt-1">
            <span className="tabular-nums" data-testid="readiness-card-progress">{progress}%</span> ready
            <span className="text-sm text-slate-500 font-normal ml-2">
              · {completed.length}/{items.length} complete
            </span>
          </h3>
        </div>
        <div className="text-[10px] text-slate-400 hidden sm:block">
          {readiness.purchase_destination_type === "WEBSITE"
            ? "Website-first merchant"
            : readiness.purchase_destination_type === "MOBILE_APP"
              ? "Mobile-app merchant"
              : "Website + app"}
        </div>
      </div>

      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 to-cyan-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
          data-testid="readiness-card-progress-bar"
        />
      </div>

      <ul className="space-y-2" data-testid="readiness-card-list">
        {items.map((item) => {
          const href = REMEDIATION[item.label];
          const done = item.status === "complete" || item.status === "active" || item.status === "connected";
          const testid = `readiness-item-${item.label.replace(/\s+/g, "-").toLowerCase()}`;
          const inner = (
            <div className="flex items-start justify-between gap-3 py-1.5 group">
              <div className="flex items-start gap-2.5 min-w-0">
                {statusIcon(item.status)}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{item.label}</div>
                  {!done && item.hint && (
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{item.hint}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {statusPill(item.status)}
                {!done && href && (
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-cyan-700 transition" />
                )}
              </div>
            </div>
          );
          if (!done && href) {
            return (
              <li key={item.label} data-testid={testid}>
                <Link
                  to={href}
                  className="block px-2 -mx-2 rounded-lg hover:bg-slate-50 transition"
                  title={item.hint || item.label}
                  data-testid={`${testid}-link`}
                >
                  {inner}
                </Link>
              </li>
            );
          }
          return <li key={item.label} data-testid={testid}>{inner}</li>;
        })}
      </ul>

      {remaining.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>Mobile app configuration is always optional — a website is enough.</span>
        </div>
      )}
    </div>
  );
}

/** Convenience wrapper — the common case: fetch once, render both. */
export default function ReadinessWidget() {
  const { data } = useReadiness();
  if (!data) return null;
  return (
    <>
      <ReadinessBanner readiness={data} />
      <ReadinessCard readiness={data} />
    </>
  );
}
